import api from './api.service';

export type UnifiedTaskSlaRisk = 'none' | 'safe' | 'warning' | 'breach' | 'critical';

/** Display label of a stored unified-task `module` value (the value 'Sales' is kept as data). */
const TASK_MODULE_LABELS: Record<string, string> = {
  Sales: 'Growth & Revenue',
  SupplyChain: 'Supply Chain',
};

export function taskModuleLabel(module?: string | null): string {
  if (!module) return '';
  return TASK_MODULE_LABELS[module] ?? module;
}

export interface UnifiedTaskRow {
  id: string;
  task_type: string;
  module: string;
  source_document_type: string;
  source_document_id: string;
  source_document_number?: string;
  party_id?: string;
  party_display_name?: string;
  priority: string;
  status: string;
  due_date?: string;
  overdue_days?: number;
  assigned_to?: string;
  assigned_to_name?: string;
  recommended_action?: string;
  action_link?: string;
  remarks?: string;
  created_at: string;
  comment_count?: number;
  sla_risk?: UnifiedTaskSlaRisk;
  is_near_breach?: boolean;
  is_breached?: boolean;
  is_hard_breached?: boolean;
  sla_remaining_seconds?: number | null;
  sla_delay_seconds?: number | null;
  expected_completion_at?: string;
  sla_warning_at?: string;
  sla_breach_at?: string;
  sla_hard_breach_at?: string;
  parent_task_id?: string;
  dependency_blocked?: boolean;
  blocking_task_ids?: string[];
  blocking_summary?: string;
  escalation_level?: number;
  delay_reason?: string;
  system_inferred_reason?: string;
  system_inferred_reason_effective?: string;
  execution_priority_score?: number;
}

export interface UnifiedTaskCommentRow {
  id: string;
  task_id: string;
  user_id?: string;
  body: string;
  created_at: string;
}

export interface UnifiedTaskHistoryEntry {
  id: string;
  event_type: 'status' | 'assignment';
  at: string;
  actor_user_id?: string;
  summary: string;
}

export interface UnifiedCompanyUserRow {
  id: string;
  name: string;
  email?: string;
}

export interface UnifiedTaskFilters {
  module?: string;
  status?: string;
  assigned_to?: string;
  overdue_only?: boolean;
  due_today_only?: boolean;
  /** Tasks with no due_date (API no_due_date_only=1) */
  no_due_date_only?: boolean;
  /** Exclude Completed / Cancelled (API open_only=1) */
  open_only?: boolean;
  unassigned_only?: boolean;
  team_others_only?: boolean;
  q?: string;
  priority?: string;
  task_type?: string;
  source_document_type?: string;
  /** API query `origin`: `manual` | `system` */
  origin?: 'manual' | 'system';
  limit?: number;
  offset?: number;
  sla_risk?: 'safe' | 'warning' | 'breach' | 'critical' | 'none';
  blocked_only?: boolean;
  ready_to_start?: boolean;
  sort?: 'default' | 'sla';
}

function unwrap<T>(res: unknown): T {
  const r = res as any;
  return (r?.data?.data ?? r?.data ?? r) as T;
}

export async function getUnifiedCompanyUsers(): Promise<UnifiedCompanyUserRow[]> {
  const res = await api.get<{ data?: UnifiedCompanyUserRow[] }>('/unified/company-users');
  const data = unwrap<UnifiedCompanyUserRow[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export async function setDocumentTaskRouting(payload: {
  source_document_type: string;
  source_document_id: string;
  next_action_user_id: string | null;
}): Promise<{ next_action_user_id: string | null; tasks_updated: number }> {
  const res = await api.put('/unified/task-routing', payload);
  return unwrap<{ next_action_user_id: string | null; tasks_updated: number }>(res);
}

export async function getUnifiedTasks(
  params?: {
    sync?: boolean;
    as_on_date?: string;
  } & UnifiedTaskFilters
): Promise<UnifiedTaskRow[]> {
  const searchParams = new URLSearchParams();
  if (params?.sync) searchParams.set('sync', '1');
  if (params?.as_on_date) searchParams.set('as_on_date', params.as_on_date);
  if (params?.module) searchParams.set('module', params.module);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.assigned_to) searchParams.set('assigned_to', params.assigned_to);
  if (params?.overdue_only) searchParams.set('overdue_only', '1');
  if (params?.due_today_only) searchParams.set('due_today_only', '1');
  if (params?.no_due_date_only) searchParams.set('no_due_date_only', '1');
  if (params?.open_only) searchParams.set('open_only', '1');
  if (params?.unassigned_only) searchParams.set('unassigned_only', '1');
  if (params?.team_others_only) searchParams.set('team_others_only', '1');
  if (params?.q?.trim()) searchParams.set('q', params.q.trim());
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.task_type?.trim()) searchParams.set('task_type', params.task_type.trim());
  if (params?.source_document_type?.trim()) {
    searchParams.set('source_document_type', params.source_document_type.trim());
  }
  if (params?.origin === 'manual' || params?.origin === 'system') searchParams.set('origin', params.origin);
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.offset != null) searchParams.set('offset', String(params.offset));
  if (params?.sla_risk) searchParams.set('sla_risk', params.sla_risk);
  if (params?.blocked_only) searchParams.set('blocked_only', '1');
  if (params?.ready_to_start) searchParams.set('ready_to_start', '1');
  if (params?.sort === 'sla') searchParams.set('sort', 'sla');
  const res = await api.get<{ data?: UnifiedTaskRow[] }>(`/unified/tasks?${searchParams.toString()}`);
  const data = unwrap<UnifiedTaskRow[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export async function getUnifiedTask(id: string): Promise<UnifiedTaskRow | null> {
  const res = await api.get<{ data?: UnifiedTaskRow }>(`/unified/tasks/${id}`);
  const data = unwrap<UnifiedTaskRow | undefined>(res);
  return data && typeof data === 'object' && data.id ? data : null;
}

export async function syncUnifiedTasks(asOnDate?: string): Promise<{ created: number; updated: number }> {
  const res = await api.post<{ data?: { created: number; updated: number } }>('/unified/tasks/sync', { as_on_date: asOnDate });
  const data = unwrap<{ created: number; updated: number } | undefined>(res);
  return data && typeof data.created === 'number' && typeof data.updated === 'number'
    ? { created: data.created, updated: data.updated }
    : { created: 0, updated: 0 };
}

export async function createUnifiedTask(body: {
  recommended_action: string;
  module: string;
  priority?: string;
  due_date?: string;
  assigned_to?: string | null;
}): Promise<UnifiedTaskRow> {
  const res = await api.post<{ data?: UnifiedTaskRow }>('/unified/tasks', body);
  const data = unwrap<UnifiedTaskRow | undefined>(res);
  if (!data?.id) throw new Error('Invalid response');
  return data;
}

export async function updateUnifiedTask(
  id: string,
  updates: {
    status?: string;
    assigned_to?: string | null;
    remarks?: string;
    delay_reason?: string | null;
    system_inferred_reason?: string | null;
  }
): Promise<UnifiedTaskRow | null> {
  const res = await api.patch<{ data?: UnifiedTaskRow }>(`/unified/tasks/${id}`, updates);
  const data = unwrap<UnifiedTaskRow | undefined>(res);
  return data && typeof data === 'object' ? data : null;
}

export async function getTaskComments(taskId: string): Promise<UnifiedTaskCommentRow[]> {
  const res = await api.get<{ data?: UnifiedTaskCommentRow[] }>(`/unified/tasks/${taskId}/comments`);
  const data = unwrap<UnifiedTaskCommentRow[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export async function postTaskComment(taskId: string, body: string): Promise<UnifiedTaskCommentRow> {
  const res = await api.post<{ data?: UnifiedTaskCommentRow }>(`/unified/tasks/${taskId}/comments`, { body });
  const data = unwrap<UnifiedTaskCommentRow | undefined>(res);
  if (!data?.id) throw new Error('Invalid response');
  return data;
}

export async function getTaskHistory(taskId: string): Promise<UnifiedTaskHistoryEntry[]> {
  const res = await api.get<{ data?: UnifiedTaskHistoryEntry[] }>(`/unified/tasks/${taskId}/history`);
  const data = unwrap<UnifiedTaskHistoryEntry[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export interface UnifiedTaskSavedViewRow {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  sort_order: number;
  updated_at: string;
}

export async function listUnifiedTaskSavedViews(): Promise<UnifiedTaskSavedViewRow[]> {
  const res = await api.get<{ data?: UnifiedTaskSavedViewRow[] }>('/unified/task-views');
  const data = unwrap<UnifiedTaskSavedViewRow[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export async function createUnifiedTaskSavedView(body: {
  name: string;
  filters: Record<string, unknown>;
}): Promise<UnifiedTaskSavedViewRow> {
  const res = await api.post<{ data?: UnifiedTaskSavedViewRow }>('/unified/task-views', body);
  const data = unwrap<UnifiedTaskSavedViewRow | undefined>(res);
  if (!data?.id) throw new Error('Invalid response');
  return data;
}

export async function updateUnifiedTaskSavedView(
  id: string,
  body: { name?: string; filters?: Record<string, unknown> }
): Promise<UnifiedTaskSavedViewRow> {
  const res = await api.patch<{ data?: UnifiedTaskSavedViewRow }>(`/unified/task-views/${id}`, body);
  const data = unwrap<UnifiedTaskSavedViewRow | undefined>(res);
  if (!data?.id) throw new Error('Invalid response');
  return data;
}

export async function deleteUnifiedTaskSavedView(id: string): Promise<void> {
  await api.delete(`/unified/task-views/${id}`);
}

export interface UnifiedTaskSlaDefinitionRow {
  id: string;
  module: string;
  task_type: string;
  source_document_type: string;
  sla_duration_minutes: number;
  warning_threshold_percent: number;
  escalation_threshold_percent: number;
  hard_breach_threshold_percent: number;
  business_hours_only: boolean;
  escalation_json: unknown;
  is_active: boolean;
  updated_at: string;
}

export async function listUnifiedTaskSlaDefinitions(): Promise<UnifiedTaskSlaDefinitionRow[]> {
  const res = await api.get<{ data?: UnifiedTaskSlaDefinitionRow[] }>('/unified/sla-definitions');
  const data = unwrap<UnifiedTaskSlaDefinitionRow[] | undefined>(res);
  return Array.isArray(data) ? data : [];
}

export async function createUnifiedTaskSlaDefinition(body: {
  module?: string;
  task_type?: string;
  source_document_type?: string;
  sla_duration_minutes: number;
  warning_threshold_percent?: number;
  escalation_threshold_percent?: number;
  hard_breach_threshold_percent?: number;
  business_hours_only?: boolean;
  escalation_json?: unknown;
  is_active?: boolean;
}): Promise<{ id: string }> {
  const res = await api.post<{ data?: { id: string } }>('/unified/sla-definitions', body);
  const data = unwrap<{ id: string } | undefined>(res);
  if (!data?.id) throw new Error('Invalid response');
  return data;
}

export async function updateUnifiedTaskSlaDefinition(
  id: string,
  body: {
    sla_duration_minutes?: number;
    warning_threshold_percent?: number;
    escalation_threshold_percent?: number;
    hard_breach_threshold_percent?: number;
    business_hours_only?: boolean;
    escalation_json?: unknown;
    is_active?: boolean;
  }
): Promise<void> {
  await api.patch(`/unified/sla-definitions/${id}`, body);
}

export async function deleteUnifiedTaskSlaDefinition(id: string): Promise<void> {
  await api.delete(`/unified/sla-definitions/${id}`);
}
